import { MdsButtonVariant } from "@/design-system/components/button";
import { MdsButton } from "@/design-system/components/button";
import { MdsIconButton } from "@/design-system/components/icon-button";
import { mdsColors } from "@/design-system/foundations";
import { MdsIconKind } from "@/design-system/components/icon";
import { MdsText, MdsTextSize, MdsTextWeight } from "@/design-system/components/text";
import { MdsModal } from "@/design-system/components/modal";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { useAppStore } from "@/store";
import { ApiKeyItemInfo } from "@/pages/integrations/api/integrations-api-page.types";

type TProps = {
  keyToDelete: ApiKeyItemInfo | null;
  setKeyToDelete: (keyToDelete: ApiKeyItemInfo | null) => void;
};

export const DeleteKeyModal = observer(({ keyToDelete, setKeyToDelete }: TProps) => {
  const { store } = useAppStore();

  const isModalOpen = !!keyToDelete;
  const { id: keyId, title: keyTitle } = keyToDelete ?? {};

  const handleCloseModal = () => {
    setKeyToDelete(null);
  };

  const handleDeleteApiKey = () => {
    setKeyToDelete(null);

    if (!keyId) {
      return;
    }

    store.apiKeys.deleteApiKey({ id: keyId });
  };

  return (
    <MdsModal rootElementId="root" isModalOpen={isModalOpen} handleCloseModal={handleCloseModal}>
      <ModalContainer>
        <Header>
          <Title>
            <MdsText size={MdsTextSize.Large} weight={MdsTextWeight.Bold}>
              {keyTitle
                ? `Are you sure you want to delete “${keyTitle}”?`
                : "Are you sure you want to delete this API key?"}
            </MdsText>
          </Title>
          <MdsIconButton iconKind={MdsIconKind.Exit} onClick={handleCloseModal} />
        </Header>
        <MdsText
          lineHeight={MdsTextSize.Small}
          color={mdsColors().grey.x600}
          size={MdsTextSize.Small}
        >
          Deletion is permanent and cannot be undone.
        </MdsText>
        <FooterStyles>
          <MdsButton
            label="Cancel"
            variant={MdsButtonVariant.TextTertiary}
            onClick={handleCloseModal}
          />
          <MdsButton
            label="Delete forever"
            variant={MdsButtonVariant.Danger}
            onClick={handleDeleteApiKey}
          />
        </FooterStyles>
      </ModalContainer>
    </MdsModal>
  );
});

const ModalContainer = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: 16,
  padding: "24px",
  maxWidth: 500,
});

const Header = styled.div({
  alignItems: "center",
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
});

const Title = styled.div({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: 6,
});

const FooterStyles = styled.div({
  alignSelf: "flex-end",
  display: "flex",
  gap: "8px",
  marginTop: "8px",
});
